import type {
  CreateReviewAction,
  OwnerPayload,
  ReviewAction,
  ReviewerRole,
  ReviewerRoleId,
  TeamId,
  UserId,
} from "platformed-client/types";
import {getContentId, getOwnerNames} from "../../../utils/owners";
import type {ReviewMap} from "./reviewsShared";

/** Identity shared by a candidate and an existing reviewer: a user or team id, never an `OwnerId`. */
export type ContentId = UserId | TeamId;

/** A person or team that can be picked for a role, plus what they already are on this page. */
export type Candidate = {
  contentId: ContentId;
  owner: OwnerPayload;
  name: string;
  /** Secondary line: email for users, member count for teams. */
  detail: string;
  kind: "User" | "Team";
  /** Name and detail joined, so one string can back the type-ahead filter. */
  searchText: string;
  state: CandidateState;
};

export type CandidateState =
  /** Assignable. */
  | {type: "Free"}
  /** Already holds the role being edited, so they arrive pre-selected. */
  | {type: "ThisRole"; review: ReviewAction}
  /** Holds a different role here; one role per person per page, so this blocks selection. */
  | {type: "OtherRole"; role: ReviewerRole; review: ReviewAction}
  /**
   * Queued into a different role on a surface that defers writes, so only the staged plans know.
   * Without it the same person could be queued twice, and the server would keep one silently.
   */
  | {type: "StagedOtherRole"; role: ReviewerRole};

/** What a reviewer currently is on the page, resolved against the configured roles. */
export type Assignment = {review: ReviewAction; role?: ReviewerRole};

/**
 * Index the page's reviewers by user/team id.
 *
 * `Reviewable.reviews` is keyed by `OwnerId`, a different identity from the `UserId`/`TeamId`
 * the account-users and teams queries return. Both are opaque strings, so comparing the wrong
 * pair type-checks and silently matches nothing; they meet on the id inside `owner_payload`.
 */
export function buildAssignments(reviews: ReviewMap, roles: ReviewerRole[]): Map<ContentId, Assignment> {
  const roleById = new Map(roles.map(role => [role.reviewer_role_id, role]));
  const byContentId = new Map<ContentId, Assignment>();
  for (const review of Object.values(reviews)) {
    const role = review.reviewer_role_id ? roleById.get(review.reviewer_role_id) : undefined;
    byContentId.set(getContentId(review.reviewer.owner_payload), {review, role});
  }
  return byContentId;
}

export function candidateState(
  assignment: Assignment | undefined,
  roleId: ReviewerRoleId,
  /** The role this candidate is queued into, if any, on a surface that defers writes. */
  stagedRole?: ReviewerRole,
): CandidateState {
  if (assignment) {
    const {review, role} = assignment;
    if (role?.reviewer_role_id === roleId) return {type: "ThisRole", review};
    if (role) return {type: "OtherRole", role, review};
    // A reviewer with no role — deleted, or added from a role-less surface — is assignable.
  }
  // This role's own queued additions are already in the draft, so only another role's block.
  if (stagedRole && stagedRole.reviewer_role_id !== roleId) return {type: "StagedOtherRole", role: stagedRole};
  return {type: "Free"};
}

/** Reviewers the page has that no configured role claims. Rendered separately so they stay reachable. */
export function unassignedReviews(reviews: ReviewMap, roles: ReviewerRole[]): ReviewAction[] {
  const knownRoleIds = new Set(roles.map(role => role.reviewer_role_id));
  return Object.values(reviews).filter(
    review => !review.reviewer_role_id || !knownRoleIds.has(review.reviewer_role_id),
  );
}

export function buildCandidates({
  owners,
  reviews,
  roles,
  roleId,
  stagedRoles,
}: {
  owners: OwnerPayload[];
  reviews: ReviewMap;
  roles: ReviewerRole[];
  roleId: ReviewerRoleId;
  /** Roles people are queued into but not yet written, keyed by user/team id. */
  stagedRoles?: Map<ContentId, ReviewerRole>;
}): Candidate[] {
  const assignments = buildAssignments(reviews, roles);
  return owners.map(owner => {
    const contentId = getContentId(owner);
    const names = getOwnerNames(owner);
    const name = names[0] ?? "";
    const detail = owner.type === "User" ? (names[1] ?? "") : "";
    return {
      contentId,
      owner,
      name,
      detail,
      kind: owner.type,
      searchText: names.join(" "),
      state: candidateState(assignments.get(contentId), roleId, stagedRoles?.get(contentId)),
    };
  });
}

/** Everyone already holding this role. */
export function selectedContentIds(candidates: Candidate[]): ContentId[] {
  return candidates.filter(c => c.state.type === "ThisRole").map(c => c.contentId);
}

export type ApplyPlan = {
  roleId: ReviewerRoleId;
  /** Carried so confirmation copy can name the role without re-looking it up. */
  roleName: string;
  /** Newly picked candidates; still need resolving to owners before they can be requested. */
  added: Candidate[];
  /** Everyone who should hold the role afterwards — what descendants converge on when cascading. */
  fullSelection: Candidate[];
  /** Deselected reviewers. Their `OwnerId` is already known, so no resolve step is needed. */
  removed: ReviewAction[];
  addToChildren: boolean;
};

/** Diff a draft selection against what the page already has for this role. */
export function planApply({
  candidates,
  draft,
  role,
  addToChildren,
}: {
  candidates: Candidate[];
  draft: ContentId[];
  role: ReviewerRole;
  addToChildren: boolean;
}): ApplyPlan {
  const drafted = new Set(draft);
  const byContentId = new Map(candidates.map(c => [c.contentId, c]));

  const added: Candidate[] = [];
  const fullSelection: Candidate[] = [];
  const removed: ReviewAction[] = [];

  for (const contentId of draft) {
    const candidate = byContentId.get(contentId);
    if (!candidate) continue;
    fullSelection.push(candidate);
    if (candidate.state.type !== "ThisRole") added.push(candidate);
  }
  for (const candidate of candidates) {
    if (candidate.state.type === "ThisRole" && !drafted.has(candidate.contentId)) {
      removed.push(candidate.state.review);
    }
  }

  return {roleId: role.reviewer_role_id, roleName: role.name, added, fullSelection, removed, addToChildren};
}

export function isEmptyPlan(plan: ApplyPlan): boolean {
  return plan.added.length === 0 && plan.removed.length === 0;
}

/** Removals that need confirming: everyone who has been asked, whether or not they answered. */
export function removalImpact(removed: ReviewAction[]): {pending: ReviewAction[]; completed: ReviewAction[]} {
  return {
    pending: removed.filter(r => r.review_status === "Pending"),
    completed: removed.filter(r => r.review_status !== "Pending"),
  };
}

export type RemovalScope = "page" | "subtree";

/**
 * What became of a plan the user applied. The picker closes on "applied" and discards the draft,
 * so a surface that queues the plan or interposes a confirmation must wait until it is settled.
 */
export type ApplyOutcome = "applied" | "cancelled";

/** One POST: the actions plus the cascade flags they must be sent with. */
export type ReviewRequest = {
  actions: CreateReviewAction[];
  cascade: {includeChildren: boolean; removeFromChildren: boolean};
};

/**
 * Split a plan into the requests that carry it out.
 *
 * Removals go first, so a failure part-way leaves the page short of reviewers rather than
 * holding ones the user meant to drop. Additions for the same person are never batched with
 * them: every row in one request shares a transaction timestamp and the server takes a
 * reviewer's latest action, so `Unrequested` and `Pending` for one owner have no defined
 * winner. {@link assertDisjoint} enforces it.
 */
export function buildRequests(
  plan: ApplyPlan,
  scope: RemovalScope,
  resolvedAdditions: {owner_id: CreateReviewAction["reviewer_id"]}[],
): ReviewRequest[] {
  assertDisjoint(plan);
  const requests: ReviewRequest[] = [];

  if (plan.removed.length > 0) {
    requests.push({
      actions: plan.removed.map(review => ({
        review_status: "Unrequested",
        reviewer_id: review.reviewer.owner_id,
        reviewer_role_id: review.reviewer_role_id ?? undefined,
      })),
      cascade: {includeChildren: false, removeFromChildren: scope === "subtree"},
    });
  }

  if (resolvedAdditions.length > 0) {
    requests.push({
      actions: resolvedAdditions.map(owner => ({
        review_status: "Pending",
        reviewer_id: owner.owner_id,
        reviewer_role_id: plan.roleId,
      })),
      cascade: {includeChildren: plan.addToChildren, removeFromChildren: false},
    });
  }

  return requests;
}

/** The owners to resolve for a plan: the whole role when cascading, so descendants converge. */
export function additionsToResolve(plan: ApplyPlan): OwnerPayload[] {
  return (plan.addToChildren ? plan.fullSelection : plan.added).map(c => c.owner);
}

/**
 * Nobody may be added to two roles in one save. Each plan is a separate request and the server
 * drops one for an existing reviewer, so the role saved first would win and the rest vanish
 * with no error.
 */
export function assertRolesDisjoint(plans: ApplyPlan[]) {
  const claimed = new Map<ContentId, string>();
  for (const plan of plans) {
    for (const candidate of plan.added) {
      const other = claimed.get(candidate.contentId);
      if (other) {
        throw new Error(
          `Cannot add ${candidate.name} to both ${other} and ${plan.roleName} in one save: a reviewer holds one role per page.`,
        );
      }
      claimed.set(candidate.contentId, plan.roleName);
    }
  }
}

function assertDisjoint(plan: ApplyPlan) {
  const removedIds = new Set(plan.removed.map(review => getContentId(review.reviewer.owner_payload)));
  const clash = plan.added.find(candidate => removedIds.has(candidate.contentId));
  if (clash) {
    throw new Error(
      `Cannot add and remove ${clash.name} in one apply: the server cannot order two actions for the same reviewer within a request.`,
    );
  }
}
